import { getSolanaRpcUrl, getHeliusRpcUrl } from "./solanaRpc";
import { fetchPumpFunProfile, type PumpFunProfile } from "./pumpfunProfile";

export type WalletStats = {
  address: string;
  solBalance: number;
  txCount: number;
  tokenCount: number;
  isHighValue: boolean;
  isActive: boolean;
  assetLogos: string[];
  topAssets?: {
    symbol: string;
    logo: string;
    amount: number;
    mint: string;
  }[];
  usdValue?: number;
  pumpProfile?: PumpFunProfile;
};

export type RecentTransaction = {
  signature: string;
  type: string;
  timestamp: number;
  amount?: number;
  token?: string;
  status: string;
};

export type TradeStats = {
  holdTimeSeconds?: number;
  profitSol?: number;
  entryPrice?: number;
};

/**
 * Fetches basic wallet intelligence from Helius.
 */
const walletCache = new Map<string, { data: WalletStats; ts: number }>();
const CACHE_TTL = 30 * 60_000; // 30 minutes
const inflightWalletStats = new Map<string, Promise<WalletStats | null>>();

export async function fetchWalletStats(address: string): Promise<WalletStats | null> {
  const cached = walletCache.get(address);
  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    return cached.data;
  }
  
  // Deduplicate concurrent inflight requests
  const inflight = inflightWalletStats.get(address);
  if (inflight) return inflight;

  const promise = (async () => {
    const heliusUrl = getHeliusRpcUrl();
    const apiKeyMatch = heliusUrl.match(/api-key=([^&]+)/);
    const apiKey = apiKeyMatch ? apiKeyMatch[1] : "c78141ce-045d-48ab-9847-405100d54a14";

    try {
      let solBalance = 0;
      let tokenCount = 0;
      let assetLogos: string[] = [];
      let txCount = 0;
      let usdValue = 0;
      let topAssets: any[] = [];

      try {
        // 1. Fetch Balance via Helius
        const balanceRes = await fetch(heliusUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: "wallet-stats",
            method: "getAccountInfo",
            params: [address, { encoding: "jsonParsed" }]
          })
        });
        
        if (balanceRes.ok) {
          const balanceData = await balanceRes.json();
          solBalance = (balanceData.result?.value?.lamports || 0) / 1e9;
        }

        // 2. Fetch Assets (DAS API)
        const assetsRes = await fetch(`https://mainnet.helius-rpc.com/?api-key=${apiKey}`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            jsonrpc: "2.0",
            id: "wallet-assets",
            method: "getAssetsByOwner",
            params: { ownerAddress: address, page: 1, limit: 1000 }
          })
        });
        
        if (assetsRes.ok) {
          const assetsData = await assetsRes.json();
          const items = assetsData.result?.items || [];
          tokenCount = assetsData.result?.total || items.length;
          assetLogos = items
            .slice(0, 5)
            .map((item: any) => item.content?.links?.image || item.content?.metadata?.image || "")
            .filter((url: string) => url && url.startsWith("http"));
            
          topAssets = items.slice(0, 5).map((item: any) => {
            const tokenInfo = item.token_info || {};
            const decimals = tokenInfo.decimals || 0;
            const balanceStr = tokenInfo.balance || "0";
            const amount = Number(balanceStr) / Math.pow(10, decimals);
            return {
              symbol: tokenInfo.symbol || item.content?.metadata?.symbol || "Unknown",
              logo: item.content?.links?.image || item.content?.metadata?.image || "",
              amount: amount,
              mint: item.id || ""
            };
          }).filter((a: any) => a.logo && a.logo.startsWith("http"));
        }

        // 3. Estimate TX count
        const activeRes = await fetch(`https://api.helius.xyz/v0/addresses/${address}/transactions?api-key=${apiKey}&limit=20`);
        if (activeRes.ok) {
          const recentTxs = await activeRes.json();
          txCount = recentTxs.length || 0;
        }
        
      } catch (heliusErr) {
        console.error(`[WALLET] Helius failed for ${address}`, heliusErr);
      }

      let pumpProfile: PumpFunProfile | undefined;
      try {
        pumpProfile = await fetchPumpFunProfile(address);
      } catch (e) {}

      const stats: WalletStats = {
        address,
        solBalance,
        txCount,
        tokenCount,
        isHighValue: solBalance > 50 || tokenCount > 100 || usdValue > 10000,
        isActive: true,
        assetLogos,
        topAssets: typeof topAssets !== 'undefined' ? topAssets : [],
        usdValue,
        pumpProfile
      };
      
      walletCache.set(address, { data: stats, ts: Date.now() });
      return stats;
    } catch (err) {
      console.error(`Failed to fetch wallet stats for ${address}:`, err);
      return null;
    }
  })();

  inflightWalletStats.set(address, promise);

  try {
    return await promise;
  } finally {
    inflightWalletStats.delete(address);
  }
}

/**
 * Fetches recent transactions for a wallet address from Helius.
 */
export async function fetchRecentTransactions(address: string, limit: number = 10): Promise<RecentTransaction[]> {
  const rpcUrl = getSolanaRpcUrl();
  let apiKey = "c78141ce-045d-48ab-9847-405100d54a14"; // Fallback
  
  try {
    const urlObj = new URL(rpcUrl, window.location.origin);
    apiKey = urlObj.searchParams.get("api-key") || apiKey;
  } catch (e) {}

  try {
    const response = await fetch(`/helius-api/v0/addresses/${address}/transactions?api-key=${apiKey}&limit=${limit}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch transactions: ${response.statusText}`);
    }
    
    const data = await response.json();
    const transactions: RecentTransaction[] = (data || []).map((tx: any) => ({
      signature: tx.signature || tx.transaction?.signatures?.[0] || "",
      type: tx.type || "Unknown",
      timestamp: tx.timestamp || Date.now(),
      amount: tx.nativeTransfers?.[0]?.amount ? tx.nativeTransfers[0].amount / 1e9 : undefined,
      token: tx.tokenTransfers?.[0]?.mint || undefined,
      status: tx.status || "Success"
    }));
    
    return transactions;
  } catch (err) {
    console.error(`Failed to fetch recent transactions for ${address}:`, err);
    return [];
  }
}

const tradeCache = new Map<string, { data: TradeStats; ts: number }>();

/**
 * Calculates hold time for a specific trade by searching for the corresponding entry.
 */
export async function fetchTradeIntelligence(address: string, mint: string, sellTsMs: number, sellPriceSolPerToken?: number): Promise<TradeStats | null> {
  const cacheKey = `${mint}:${address}:${sellTsMs}`;
  const cached = tradeCache.get(cacheKey);
  if (cached && Date.now() - cached.ts < CACHE_TTL) {
    return cached.data;
  }

  const rpcUrl = getSolanaRpcUrl();
  let apiKey = "c78141ce-045d-48ab-9847-405100d54a14";
  
  try {
    const urlObj = new URL(rpcUrl, window.location.origin);
    apiKey = urlObj.searchParams.get("api-key") || apiKey;
  } catch (e) {}

  try {
    const url = `/helius-api/v0/addresses/${address}/transactions?api-key=${apiKey}`;
    const res = await fetch(url);
    const data = await res.json();
    
    // Find the most recent BUY before this sellTsMs
    let entryTsMs: number | null = null;
    let entrySolSpent = 0;
    let entryTokensReceived = 0;
    
    for (const tx of data) {
      const tsMs = tx.timestamp * 1000;
      if (tsMs >= sellTsMs) continue; // Skip trades after or exactly at this sell

      const isBuy = tx.description?.includes("bought") || tx.events?.swap?.nativeInput !== null;
      // Check if this tx involves the target mint
      const involvesMint = tx.tokenTransfers?.some((tr: any) => tr.mint === mint) || tx.description?.includes(mint);

      if (involvesMint && isBuy) {
        entryTsMs = tsMs;
        // Try to extract exact SOL spent and tokens received
        if (tx.events?.swap) {
          entrySolSpent = (tx.events.swap.nativeInput?.amount || 0) / 1e9;
          const tokenOut = tx.events.swap.tokenOutputs?.find((t: any) => t.mint === mint);
          if (tokenOut) entryTokensReceived = tokenOut.tokenAmount;
        }
        break; // Found the entry
      }
    }

    if (entryTsMs) {
      const stats: TradeStats = {
        holdTimeSeconds: Math.floor((sellTsMs - entryTsMs) / 1000)
      };
      
      if (entrySolSpent > 0 && entryTokensReceived > 0) {
        stats.entryPrice = entrySolSpent / entryTokensReceived;
        if (sellPriceSolPerToken) {
          stats.profitSol = (sellPriceSolPerToken - stats.entryPrice) / stats.entryPrice; // store as ROI % decimal
        }
      }
      
      return stats;
    }
    
    return null;
  } catch (err) {
    return null;
  }
}
